import { Button, Stack } from '@mui/material';
import { PlusIcon } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from 'react-beautiful-dnd';
import { useFieldArray, useFormContext } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { DndListWrapper } from '../../../components/uikit/Dnd/dnd.styled';
import { SELECT_LABEL_MAX_BODY_LENGTH } from '../SchoolCustomFields/constants';
import { ConductTypeForm, revalidateOptionLabels } from './SchoolConductTypeModalContent';
import { SchoolConductTypeSelectRow } from './SchoolConductTypeSelectRow';

interface SchoolConductTypeSelectProps {
  showArchived: boolean;
  onArchiveOption?: () => void;
}

export const SchoolConductTypeSelect: FC<SchoolConductTypeSelectProps> = ({
  showArchived,
  onArchiveOption,
}) => {
  const { formatMessage } = useIntl();
  const { control, watch, formState, trigger } = useFormContext<ConductTypeForm>();
  const selectTypeOptions = watch('select_type_options');

  const { move, update, remove, insert } = useFieldArray({
    control: control,
    name: 'select_type_options',
  });

  const activeSelectOptions = useMemo(() => {
    return selectTypeOptions?.filter((option) => !option.archived);
  }, [selectTypeOptions]);

  const archivedSelectOptions = useMemo(() => {
    return selectTypeOptions?.filter((option) => option.archived);
  }, [selectTypeOptions]);

  const handleDragEnd = useCallback<OnDragEndResponder>(
    (result) => {
      if (!result.destination) {
        return;
      }

      move(result.source.index, result.destination.index);
    },
    [move],
  );

  const handleAddSelectOption = useCallback(() => {
    insert(activeSelectOptions?.length || 0, {
      id: uuidv4(),
      order: activeSelectOptions?.length ?? 0,
      original_order: selectTypeOptions?.length ?? 0,
      label: '',
      archived: false,
      saved: false,
    });
  }, [activeSelectOptions, selectTypeOptions, insert]);

  const handleArchiveSelectOption = useCallback(
    async (id: string) => {
      const option = selectTypeOptions?.find((option) => option.id === id);
      const optionIdx = selectTypeOptions?.findIndex((option) => option.id === id);

      if (!option) return;

      //without await, the validation is not always properly triggered
      await update(optionIdx, {
        ...option,
        archived: true,
      });

      move(optionIdx, selectTypeOptions.length - 1);

      onArchiveOption?.();

      revalidateOptionLabels({
        handleTrigger: trigger,
        errors: formState.errors,
      });
    },
    [selectTypeOptions, update, move, onArchiveOption, trigger, formState.errors],
  );

  const handleRestoreSelectOption = useCallback(
    async (id: string) => {
      const option = selectTypeOptions?.find((option) => option.id === id);
      const optionIdx = selectTypeOptions?.findIndex((option) => option.id === id);

      if (!option) return;

      //without await, the validation is not always properly triggered
      await update(optionIdx, {
        ...option,
        order: activeSelectOptions.length,
        archived: false,
      });

      move(optionIdx, activeSelectOptions.length);

      revalidateOptionLabels({
        handleTrigger: trigger,
        errors: formState.errors,
      });
    },
    [selectTypeOptions, update, activeSelectOptions.length, move, trigger, formState.errors],
  );

  const handleDeleteSelectOption = async (id: number) => {
    //without await, the validation is not always properly triggered
    await remove(id);

    revalidateOptionLabels({
      handleTrigger: trigger,
      errors: formState.errors,
    });
  };

  const validateOptionLabel = useCallback(
    (id: string, value: string) => {
      const duplicates = selectTypeOptions.filter(
        (option) => option.id !== id && option.label.toLowerCase() === value.toLowerCase(),
      );

      const hasArchivedDuplicates = duplicates.some((option) => option.archived);
      const isArchived = !!selectTypeOptions.find((option) => option.id === id)?.archived;

      if (duplicates.length) {
        return formatMessage({
          id:
            hasArchivedDuplicates && !isArchived
              ? 'school-sections-CustomFields-Message-OptionNameExistsInArchived'
              : 'school-sections-CustomFields-Message-OptionNameExists',
        });
      }

      return true;
    },
    [formatMessage, selectTypeOptions],
  );

  // TODO: should migrate to react dnd
  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="ConductTypeOptionsDroppable">
          {(provided) => (
            <DndListWrapper
              {...provided.droppableProps}
              ref={provided.innerRef}
              gap={1}
              sx={{ overflowX: 'hidden' }}
            >
              {activeSelectOptions.map((option, index) => (
                <Draggable key={option.id} draggableId={option?.id ?? ''} index={index}>
                  {(provided) => (
                    <SchoolConductTypeSelectRow
                      id={option.id ?? ''}
                      provided={provided}
                      key={option.id}
                      maxBodyLength={SELECT_LABEL_MAX_BODY_LENGTH}
                      onArchiveOption={handleArchiveSelectOption}
                      onRestoreOption={handleRestoreSelectOption}
                      onDeleteOption={handleDeleteSelectOption}
                      option={option}
                      optionList={activeSelectOptions}
                      validate={validateOptionLabel}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}

              <Button
                variant="outlined"
                startIcon={<PlusIcon />}
                onClick={handleAddSelectOption}
                sx={{
                  '&.Mui-disabled': {
                    backgroundColor: (theme) => theme.palette.background.default,
                  },
                  width: '133px',
                }}
              >
                <FormattedMessage id="action-AddOption" />
              </Button>
            </DndListWrapper>
          )}
        </Droppable>
      </DragDropContext>

      {showArchived && (
        <Stack direction="column" gap={1} mt={4}>
          {archivedSelectOptions.map((option, index) => (
            <SchoolConductTypeSelectRow
              id={option.id ?? ''}
              key={option.id}
              maxBodyLength={SELECT_LABEL_MAX_BODY_LENGTH}
              onArchiveOption={handleArchiveSelectOption}
              onRestoreOption={handleRestoreSelectOption}
              onDeleteOption={handleDeleteSelectOption}
              option={option}
              optionList={activeSelectOptions}
              validate={validateOptionLabel}
            />
          ))}
        </Stack>
      )}
    </>
  );
};
